import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './RenewalModal.css';
import { createApiUrl } from '../utils/apiConfig';

const RenewalModal = ({ isOpen, onClose, currentSubscription, onRenewalSuccess }) => {
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);

  
  // Alert to confirm component is being called
  if (isOpen) {
  }

  useEffect(() => {
    if (isOpen) {
      fetchAvailablePlans();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when modal opens only
  }, [isOpen]);

  const fetchAvailablePlans = async () => {
    try {
      setPlansLoading(true);
      const response = await axios.get(createApiUrl('/api/plans'), {
        withCredentials: true
      });
      
      // Filter for subscription plans only (exclude workshops and trials)
      const subscriptionPlans = response.data.filter(plan => 
        plan.type === 'subscription' && 
        plan.name.includes('Year Subscription') &&
        plan.assets && plan.assets.length > 0
      );
      
      setAvailablePlans(subscriptionPlans);
      
      // Pre-select the current plan if it exists
      const currentPlan = subscriptionPlans.find(plan => 
        plan.name === currentSubscription?.planName || 
        plan.name === currentSubscription?.planId?.name
      );
      if (currentPlan) {
        setSelectedPlan(currentPlan);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setPlansLoading(false);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  const handleRenewal = async () => {
    
    if (!selectedPlan) {
      alert('Please select a subscription plan');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(createApiUrl(`/api/renew-subscription/${currentSubscription._id}`), {
        selectedPlanId: selectedPlan._id
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        if (response.data.redirectTo) {
          // Redirect to basket for payment
          window.location.href = response.data.redirectTo;
        } else {
          alert('Subscription renewed successfully!');
          onRenewalSuccess();
          onClose();
        }
      } else {
        alert(response.data.message || 'Failed to renew subscription');
      }
    } catch (error) {
      console.error('Error renewing subscription:', error);
      alert('Error renewing subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const getDurationText = (duration) => {
    if (duration >= 365) {
      const years = Math.round(duration / 365);
      return `${years} Year${years > 1 ? 's' : ''}`;
    } else if (duration >= 30) {
      const months = Math.round(duration / 30);
      return `${months} Month${months > 1 ? 's' : ''}`;
    } else {
      return `${duration} Day${duration > 1 ? 's' : ''}`;
    }
  };

  
  if (!isOpen) {
    return null;
  }

  return (
    <div className="renewal-modal-overlay">
      <div className="renewal-modal">
        <div className="renewal-modal-header">
          <h3>Renew Subscription</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="renewal-modal-body">
          <div className="current-subscription-info">
            <h4>Current Subscription</h4>
            <p><strong>Plan:</strong> {currentSubscription?.planName || currentSubscription?.planId?.name}</p>
            <p><strong>Status:</strong> {currentSubscription?.status}</p>
            <p><strong>End Date:</strong> {new Date(currentSubscription?.endDate).toLocaleDateString()}</p>
          </div>

          <div className="available-plans">
            <h4>Choose Your Renewal Plan</h4>
            {plansLoading ? (
              <div className="loading">Loading available plans...</div>
            ) : (
              <div className="plans-grid">
                {availablePlans.map((plan) => (
                  <div 
                    key={plan._id} 
                    className={`plan-card ${selectedPlan?._id === plan._id ? 'selected' : ''}`}
                    onClick={() => handlePlanSelect(plan)}
                  >
                    <div className="plan-header">
                      <h5>{plan.name}</h5>
                      <div className="plan-price">{formatPrice(plan.price)}</div>
                    </div>
                    <div className="plan-details">
                      <p><strong>Duration:</strong> {getDurationText(plan.duration)}</p>
                      <p><strong>Assets:</strong> {plan.assets?.length || 0} lessons</p>
                      {plan.description && (
                        <p className="plan-description">{plan.description}</p>
                      )}
                    </div>
                    {selectedPlan?._id === plan._id && (
                      <div className="selected-indicator">✓ Selected</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="renewal-modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleRenewal}
            disabled={!selectedPlan || loading}
            style={{
              backgroundColor: 'var(--darkPurple)',
              borderColor: 'var(--darkOrange)',
              color: 'var(--darkOrange)',
              fontWeight: 'bold'
            }}
          >
            {loading ? 'Processing...' : 'Renew Subscription'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RenewalModal;
